












































import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { create } from "vue-modal-dialogs";
import { getModule } from "vuex-module-decorators";

import ActionButton from "@/lib/components/ActionButton.vue";
import FormErrors from "@/lib/components/FormErrors.vue";

import dataStore from "@/store";
import ErrorConverterService from "@/modules/forms/error-converter.service";

import CreateShippingManifestDialog from "./CreateShippingManifestDialog.vue";

import BulkShipmentStore from "../store";
import BulkShipment from "../bulk-shipment.model";
import { BulkShipmentStatusValue } from "../bulk-shipment-status.value";

@Component({
  components: {
    FormErrors,
    ActionButton,
  },
})
export default class ShipmentMassActionsCard extends Vue {
  @Prop({ required: true })
  public readonly selectedShipments!: string[];

  @Prop({ required: true })
  public readonly availableShipments!: BulkShipment[];

  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;

  private fIsSubmitting = false;
  private fSubmitErrors: string[] = [];

  private fBulkShipmentStore: BulkShipmentStore;

  constructor() {
    super();
    this.fBulkShipmentStore = getModule(BulkShipmentStore, dataStore);
  }

  get isSubmitting(): boolean {
    return this.fIsSubmitting;
  }

  get isSelectAllPublishedDisabled(): boolean {
    return this.fIsSubmitting || this.publishedShipments.length === 0;
  }

  get isShipDisabled(): boolean {
    return this.fIsSubmitting || this.selectedShipments.length === 0;
  }

  get submitErrors(): string[] {
    return this.fSubmitErrors;
  }

  get publishedShipments(): BulkShipment[] {
    return this.availableShipments.filter(
      (shipment) => shipment.status === BulkShipmentStatusValue.PUBLISHED
    );
  }

  get isSelectNoneAvailable(): boolean {
    return this.selectedShipments.length > 0;
  }

  public selectAllPublishedShipments(): void {
    const publishedIds = this.publishedShipments.map((shipment) => shipment.id);
    this.$emit("selection-changed", publishedIds);
  }

  public clearSelection(): void {
    this.$emit("selection-changed", []);
  }

  public async onShipClick(): Promise<void> {
    if (!this.selectedShipments.length || this.isShipDisabled) {
      return;
    }

    this.fIsSubmitting = true;
    this.fSubmitErrors = [];

    try {
      const validationResult = this.validateSelectedShipments();

      if (!validationResult.isValid) {
        this.fSubmitErrors = validationResult.errors;
        return;
      }

      this.$emit("processing-started");
      const selectedShipmentObjects = this.availableShipments.filter(
        (shipment) => this.selectedShipments.includes(shipment.id)
      );

      const factoryId = selectedShipmentObjects[0].factory;

      const isCreated = await this.showCreateManifestDialog({
        factoryId,
        bulkShipmentsIds: this.selectedShipments,
      });

      if (!isCreated) {
        return;
      }

      this.$emit("action-processed");
    } catch (error) {
      this.fSubmitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsSubmitting = false;
      this.$emit("processing-finished");
    }
  }

  private async showCreateManifestDialog(data: {
    factoryId: string;
    bulkShipmentsIds: string[];
  }): Promise<boolean> {
    const dialog = create(CreateShippingManifestDialog);

    return dialog({
      factoryId: data.factoryId,
      bulkShipmentsIds: data.bulkShipmentsIds,
    });
  }

  private validateSelectedShipments(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const selectedShipmentObjects = this.availableShipments.filter((shipment) =>
      this.selectedShipments.includes(shipment.id)
    );

    if (selectedShipmentObjects.length === 0) {
      return { isValid: false, errors: ["No shipments selected"] };
    }

    const nonPublishedShipments = selectedShipmentObjects.filter(
      (shipment) => shipment.status !== BulkShipmentStatusValue.PUBLISHED
    );

    if (nonPublishedShipments.length > 0) {
      errors.push(
        `Shipments not in PUBLISHED state: ${nonPublishedShipments
          .map((s) => s.id)
          .join(", ")}`
      );
    }

    const factoryIds = Array.from(
      new Set(selectedShipmentObjects.map((shipment) => shipment.factory))
    );

    if (factoryIds.length > 1) {
      errors.push(`All shipments must belong to the same factory.`);
    }

    const distributionCenterIds = Array.from(
      new Set(
        selectedShipmentObjects.map((shipment) => shipment.distributionCenter)
      )
    );

    if (distributionCenterIds.length > 1) {
      errors.push(`All shipments must be for the same Distribution Center.`);
    }

    return { isValid: errors.length === 0, errors };
  }
}
